import { useEffect, useState } from 'react';
import { chainManager } from '../ChainManager';

export const useInactiveListener = (suppress = false) => {
  const [chain, setChain] = useState(chainManager.getCurrentChain());

  // Keep local chain state in sync with ChainManager to rebind listeners on change
  useEffect(() => {
    const unsubscribe = chainManager.on('chainChanged', (newChain: any) => {
      setChain(newChain);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (suppress) return;

    const currentChain = chain;
    const adapter = chainManager.getCurrentAdapter();

    // Strategy map for chain-specific listeners
    const setupListenersByChain: Record<string, () => (() => void) | void> = {
      bnb: () => {
        if (typeof window === 'undefined') return;

        // Use the same MetaMask detection logic as BNBAdapter
        const findMetaMaskProvider = () => {
          let ethereumProvider: any = null;
          const ethereum = (window as any).ethereum;

          if (ethereum?.isMetaMask && !ethereum.isPhantom) {
            ethereumProvider = ethereum;
          }

          if (!ethereumProvider && Array.isArray(ethereum?.providers)) {
            ethereumProvider =
              ethereum.providers.find(
                (p: any) => p && p.isMetaMask && !p.isPhantom,
              ) || null;
          }

          if (!ethereumProvider && Array.isArray(ethereum?.detected)) {
            ethereumProvider =
              ethereum.detected.find(
                (p: any) => p && p.isMetaMask && !p.isPhantom,
              ) || null;
          }

          if (!ethereumProvider && Array.isArray((window as any).ethereum)) {
            ethereumProvider =
              (window as any).ethereum.find(
                (p: any) => p && p.isMetaMask && !p.isPhantom,
              ) || null;
          }

          return ethereumProvider;
        };

        const ethereumProvider = findMetaMaskProvider();
        if (!ethereumProvider) return;

        const handleConnect = () => {
          // Only sync wallet info, don't auto-connect
          chainManager.getWalletInfo().catch(err => {
            console.debug('sync wallet info ignored', err);
          });
        };
        const handleDisconnect = () => {
          chainManager.disconnectWallet().catch(err => {
            console.debug('disconnect ignored', err);
          });
        };
        const handleChainChanged = (_chainId: string) => {
          // Ignore chain changes to prevent auto-connect
          return;
        };
        const handleAccountsChanged = (accounts: string[]) => {
          if (accounts.length === 0) {
            chainManager.disconnectWallet().catch(err => {
              console.debug('disconnect ignored', err);
            });
          } else {
            chainManager.getWalletInfo().catch(err => {
              console.debug('sync wallet info ignored', err);
            });
          }
        };

        ethereumProvider.on('connect', handleConnect);
        ethereumProvider.on('disconnect', handleDisconnect);
        ethereumProvider.on('chainChanged', handleChainChanged);
        ethereumProvider.on('accountsChanged', handleAccountsChanged);

        return () => {
          ethereumProvider.removeListener('connect', handleConnect);
          ethereumProvider.removeListener('disconnect', handleDisconnect);
          ethereumProvider.removeListener('chainChanged', handleChainChanged);
          ethereumProvider.removeListener(
            'accountsChanged',
            handleAccountsChanged,
          );
        };
      },
      solana: () => {
        if (typeof window === 'undefined') return;

        const phantom = (window as any).solana;
        if (!phantom?.isPhantom) {
          console.debug('SolanaAdapter: Phantom wallet not found');
          return;
        }

        console.debug('SolanaAdapter: Setting up Phantom wallet listeners');

        const handleConnect = () => {
          console.debug('SolanaAdapter: Phantom wallet connected');
          chainManager.getWalletInfo().catch(err => {
            console.debug('SolanaAdapter: sync wallet info ignored', err);
          });
        };

        const handleDisconnect = () => {
          console.debug('SolanaAdapter: Phantom wallet disconnected');
          chainManager.disconnectWallet().catch(err => {
            console.debug('SolanaAdapter: disconnect ignored', err);
          });
        };

        const handleAccountChange = (publicKey: any) => {
          if (publicKey) {
            console.debug(
              'SolanaAdapter: Account changed, syncing wallet info',
            );
            chainManager.getWalletInfo().catch(err => {
              console.debug('SolanaAdapter: sync wallet info ignored', err);
            });
          } else {
            console.debug('SolanaAdapter: Account disconnected');
            chainManager.disconnectWallet().catch(err => {
              console.debug('SolanaAdapter: disconnect ignored', err);
            });
          }
        };

        phantom.on('connect', handleConnect);
        phantom.on('disconnect', handleDisconnect);
        phantom.on('accountChanged', handleAccountChange);

        return () => {
          phantom.removeListener('connect', handleConnect);
          phantom.removeListener('disconnect', handleDisconnect);
          phantom.removeListener('accountChanged', handleAccountChange);
        };
      },
    };

    const cleanup = setupListenersByChain[currentChain]?.();

    // Set up chain manager event listeners
    const handleWalletChange = (walletInfo: any) => {
      // Wallet change events are handled by the global state manager
    };

    const handleChainChange = (chain: string) => {
      // Chain change events are handled by the global state manager
    };

    const unsubscribeWallet = chainManager.on(
      'walletChanged',
      handleWalletChange,
    );
    const unsubscribeChain = chainManager.on('chainChanged', handleChainChange);

    return () => {
      if (typeof cleanup === 'function') cleanup();
      unsubscribeWallet();
      unsubscribeChain();
    };
  }, [suppress, chain]);
};
